'use client';

import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { HERO_VIDEO } from '@/lib/media';
import { cn } from '@/lib/utils';

interface HeroVideoProps {
  className?: string;
  /** Descripción para AT (el video es decorativo por default). */
  ariaLabel?: string;
}

/**
 * HeroVideo — video responsive para `/manifiesto`.
 *
 * - Elige variante según matchMedia (mobile portrait / desktop landscape)
 * - `prefers-reduced-motion` → renderiza solo el poster (imagen estática)
 * - Autoplay + muted + loop + playsinline (política de browsers modernos)
 * - `preload="metadata"` para no bloquear LCP
 */
export function HeroVideo({ className, ariaLabel = 'Film manifiesto NQORO' }: HeroVideoProps) {
  const reduced = useReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [variant, setVariant] = useState<'desktop' | 'mobile'>('desktop');

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setVariant(mq.matches ? 'mobile' : 'desktop');
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (reduced) return;
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {
      // autoplay bloqueado → el poster queda visible, sin romper UX
    });
  }, [reduced, variant]);

  const source = HERO_VIDEO[variant];

  if (reduced) {
    return (
      <img
        src={source.poster}
        alt={ariaLabel}
        className={cn('h-full w-full object-cover', className)}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      key={variant}
      className={cn('h-full w-full object-cover', className)}
      autoPlay
      playsInline
      muted
      loop
      preload="auto"
      poster={source.poster}
      aria-label={ariaLabel}
      src={source.mp4}
    />
  );
}
